import { Megaphone, ShoppingBag } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { SectionHeader } from '@/components/ui/section-header';
import { SoftCard } from '@/components/ui/soft-card';

const CURRENCY_SYMBOLS = { gbp: '£', usd: '$', eur: '€' };

const formatAmount = (amount, currency) => {
	if (amount == null) return null;
	const symbol = CURRENCY_SYMBOLS[currency?.toLowerCase()] ?? '';
	const formatted = Number.isInteger(amount) ? amount.toString() : amount.toFixed(2);
	return `${symbol}${formatted}`;
};

/**
 * Add-ons that bill on top of the base plan — Local Ads (daily ad spend) and
 * any per-channel charges sourced from the live billing entitlements block.
 * Empty state when nothing's live.
 */
export function AddOnsSection({ billing, localAdsBudget, localAdsCurrency }) {
	const items = buildAddOnItems(billing, localAdsBudget, localAdsCurrency);

	return (
		<section>
			<SectionHeader
				eyebrow="Add-ons"
				title="What else you're paying for"
				sub="Channels and Local Ads billed alongside your base plan."
			/>
			<SoftCard className="!p-5 border-border">
				{items.length === 0 ? (
					<div className="rounded-xl border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground">
						No add-ons yet — enable a paid channel or set a Local Ads budget to see it here.
					</div>
				) : (
					<ul className="divide-y divide-border">
						{items.map((item) => (
							<AddOnRow key={item.id} item={item} />
						))}
					</ul>
				)}
			</SoftCard>
		</section>
	);
}

function buildAddOnItems(billing, localAdsBudget, localAdsCurrency) {
	const items = [];

	if (localAdsBudget && localAdsBudget > 0) {
		items.push({
			id: 'local-ads',
			icon: Megaphone,
			title: 'Local Ads',
			description: `${formatAmount(localAdsBudget, localAdsCurrency) ?? `${localAdsBudget}`} per day, billed daily as you spend.`,
			right: <Badge variant="action">Live</Badge>
		});
	}

	for (const [type, decision] of Object.entries(billing?.entitlements ?? {})) {
		if (decision?.policy !== 'paid') continue;
		if (!decision?.prices?.monthly) continue;
		const amount = decision.prices.monthly.unit_amount / 100;
		items.push({
			id: `channel-${type}`,
			icon: ShoppingBag,
			title: humaniseChannel(type),
			description: `${formatAmount(amount, decision.prices.monthly.currency)} per month, per location.`,
			right: <Badge variant="secondary">{decision.includedSlot ? 'Included' : 'Add-on'}</Badge>
		});
	}

	return items;
}

function humaniseChannel(type) {
	return type.replace(/-/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

function AddOnRow({ item }) {
	const Icon = item.icon;
	return (
		<li className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
			<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary-soft-foreground">
				<Icon className="h-5 w-5" />
			</div>
			<div className="min-w-0 flex-1">
				<div className="text-sm font-semibold text-foreground">{item.title}</div>
				<div className="text-xs text-muted-foreground">{item.description}</div>
			</div>
			{item.right}
		</li>
	);
}
